import { useEffect, useLayoutEffect } from 'react'
import { toast } from 'sonner'
import type { DealProposalPanelActions } from '../useDealDetailPanel'
import { ProposalStudio } from '../../ProposalStudio'
import { PlanEditor } from '../../PlanEditor'
import type { Deal, Proposta, PropostaItem } from '@/types'
import type { PlanCatalogScope } from '@/utils/planCatalog'
import type { LayoutConfig } from '@/utils/dealCalculations'
import { PlanSelector } from './PlanSelector'
import { ProposalForm } from './ProposalForm'
import { ProposalsList } from './ProposalsList'
import { SavedProposalsStrip } from './SavedProposalsStrip'
import { ExportSection } from './ExportSection'
import { ComboVariationSection } from './ComboVariationSection'
import { DealProposalSummary } from './DealProposalSummary'
import { parseComboVariationsFromDados } from './proposalStudioSave'
import { RenegociacaoProposalBanner } from './RenegociacaoProposalBanner'
import { ProposalPublicLinkCard } from './ProposalPublicLinkCard'
import { FixPropostaButton } from './FixPropostaButton'
import { findProposalWithPublicLink } from './proposalPublicLink'
import { useDealProposalTab } from './useDealProposalTab'
import { findSentProposals, isRenegotiationProposalStage, showProposalPlanSelector } from './proposalRenegotiation'
import { PROPOSAL_PDF_EXPORT_ENABLED, PROPOSAL_PPTX_EXPORT_ENABLED, PROPOSAL_STUDIO_ENABLED, PROPOSAL_FORM_ACTIONS_ENABLED } from './proposalFeatureFlags'
import { hasDealPlataforma, PLATAFORMA_REQUIRED_MESSAGE } from '@/utils/dealPlataformaRequired'

export function DealProposalTab({
  deal,
  onDealPatched,
  catalogScope = 'deals',
  onProposalStateChange,
  onRegisterProposalActions,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
  catalogScope?: PlanCatalogScope
  onProposalStateChange?: (state: { proposals: Proposta[]; draftItens: PropostaItem[] }) => void
  onRegisterProposalActions?: (actions: DealProposalPanelActions | null) => void
}) {
  const tab = useDealProposalTab(deal, onDealPatched, { catalogScope })
  const plataformaOk = hasDealPlataforma(deal.plataforma)
  const blockSentWithoutPlataforma = () => {
    if (plataformaOk) return false
    toast.error(PLATAFORMA_REQUIRED_MESSAGE)
    return true
  }

  useEffect(() => {
    onProposalStateChange?.({ proposals: tab.proposals, draftItens: tab.itens })
  }, [onProposalStateChange, tab.proposals, tab.itens])

  useLayoutEffect(() => {
    if (!onRegisterProposalActions) return
    onRegisterProposalActions({
      flushSave: async (status) => {
        if (status === 'enviada' && blockSentWithoutPlataforma()) return false
        return tab.flushProposalSave(status)
      },
    })
    return () => onRegisterProposalActions(null)
  }, [onRegisterProposalActions, tab.flushProposalSave, plataformaOk])
  const editingReadonly =
    deal.etapa === 'renegociacao' &&
    Boolean(
      tab.editingId &&
        tab.proposals.find(
          (p) =>
            p.id === tab.editingId &&
            (p.status === 'enviada' || p.status === 'aceita'),
        ),
    )
  const formCanEdit = tab.canEdit && !editingReadonly
  const sentProposals = findSentProposals(tab.proposals)
  const renegotiationEditMode = isRenegotiationProposalStage(deal.etapa)
  const editExistingProposal = renegotiationEditMode && sentProposals.length > 0
  const showPlanSelector = showProposalPlanSelector(formCanEdit, editExistingProposal, tab.showForm)
  const publicProposal = findProposalWithPublicLink(tab.proposals) ?? sentProposals[0]
  const showPublicLinkCard =
    (deal.etapa === 'proposta_enviada' || deal.etapa === 'ganho') && sentProposals.length > 0

  const openStudioForItem = (item: PropostaItem) => {
    const withProposal = item as PropostaItem & { propostaId?: string }
    if (withProposal.propostaId || tab.editingId) {
      tab.setStudioPlan({
        ...item,
        propostaId: withProposal.propostaId ?? tab.editingId ?? undefined,
      } as PropostaItem & { propostaId?: string })
    } else {
      tab.setStudioOpen(true)
    }
  }

  return (
    <div className="space-y-4">
      <DealProposalSummary deal={deal} onDealPatched={onDealPatched} />

      {showPublicLinkCard && publicProposal && (
        <ProposalPublicLinkCard proposal={publicProposal} />
      )}

      {sentProposals.length > 0 && deal.empresaId && (
        <div className='flex justify-end'>
          <FixPropostaButton dealId={deal.id} disabled={deal.etapa === 'perdido'} />
        </div>
      )}

      {renegotiationEditMode && (
        <RenegociacaoProposalBanner
          proposals={tab.proposals}
          onEditDraft={tab.handleEditProposal}
          onEnsureDraft={async () => {
            const refreshed = await tab.ensureRenegotiationDraft()
            if (!refreshed) return
            const draft = refreshed.find((p) => p.status === 'rascunho')
            if (draft) tab.handleEditProposal(draft)
          }}
          showForm={tab.showForm}
          editingDraftId={tab.editingId}
          preparingDraft={tab.preparingRenegotiationDraft || tab.loadingProposals}
          variant={deal.etapa === 'ajustar_proposta' ? 'ajustar_proposta' : 'renegociacao'}
        />
      )}

      {showPlanSelector && (
        <div className="space-y-2">
          <PlanSelector
            catalogScope={catalogScope}
            onPlanAdded={(items, planName) => void tab.handlePlanItemsAdded(items, planName)}
          />
          {!editExistingProposal && tab.showForm && tab.proposals.length > 0 && (
            <SavedProposalsStrip
              proposals={tab.proposals}
              loading={tab.loadingProposals}
              editingId={tab.editingId}
              canEdit={tab.canEdit}
              allowNew={!renegotiationEditMode}
              onSelect={tab.handleEditProposal}
              onNew={() => {
                tab.resetForm()
                tab.setShowForm(true)
              }}
            />
          )}
        </div>
      )}

      {tab.canEdit && editExistingProposal && tab.showForm && tab.proposals.length > 0 && (
        <SavedProposalsStrip
          proposals={tab.proposals}
          loading={tab.loadingProposals}
          editingId={tab.editingId}
          canEdit={tab.canEdit}
          allowNew={false}
          onSelect={tab.handleEditProposal}
          onNew={() => {
            tab.resetForm()
            tab.setShowForm(true)
          }}
        />
      )}

      {!tab.showForm && (
        <ProposalsList
          proposals={tab.proposals}
          loading={tab.loadingProposals}
          canEdit={tab.canEdit}
          allowNew={!editExistingProposal}
          dealEtapa={deal.etapa}
          showForm={tab.showForm}
          editingId={tab.editingId}
          onToggleForm={() => {
            if (tab.showForm && !tab.editingId) {
              tab.resetForm()
              tab.setShowForm(false)
            } else {
              tab.resetForm()
              tab.setShowForm(true)
            }
          }}
          onEdit={tab.handleEditProposal}
          onDelete={(id) => void tab.handleDeleteProposal(id)}
          onCopyLink={tab.handleCopyLink}
          onOpenStudio={PROPOSAL_STUDIO_ENABLED ? openStudioForItem : undefined}
          onExportPPTX={PROPOSAL_PPTX_EXPORT_ENABLED ? (p) => void tab.handleExportPPTX(p) : undefined}
          onExportPDF={PROPOSAL_PDF_EXPORT_ENABLED ? (p) => void tab.handleExportPDF(p) : undefined}
        />
      )}

      {tab.showForm && (
        <ProposalForm
          editingId={tab.editingId}
          itens={tab.itens}
          combos={tab.combos}
          comboValues={tab.comboValues}
          onCloseEditor={() => {
            if (!tab.editingId) tab.setShowForm(false)
          }}
          validadeAte={tab.validadeAte}
          observacoes={tab.observacoes}
          desconto={tab.desconto}
          comboVariations={tab.comboVariations}
          saving={tab.saving}
          generatingLink={tab.generatingLink}
          canEdit={formCanEdit}
          onValidadeChange={tab.setValidadeAte}
          onDescontoChange={tab.setDesconto}
          onObservacoesChange={tab.setObservacoes}
          onRemoveItem={tab.handleRemoveItem}
          onUpdateQty={tab.handleUpdateItemQty}
          onAddCombo={tab.handleAddCombo}
          onToggleComboItem={tab.handleToggleComboItem}
          onComboDesconto={tab.handleComboDesconto}
          onRemoveCombo={tab.handleRemoveCombo}
          onSetComboFinalPrice={tab.handleSetComboFinalPrice}
          onCancelEdit={() => {
            tab.resetForm()
            tab.setShowForm(false)
          }}
          onSaveRascunho={() => void tab.saveProposal('rascunho')}
          onEnviarProposta={() => {
            if (blockSentWithoutPlataforma()) return
            void tab.saveProposal('enviada')
          }}
          onGerarLink={() => {
            if (blockSentWithoutPlataforma()) return
            void tab.handleGerarLink()
          }}
          onEditItem={(_, index) => tab.setEditingItemIndex(index)}
          onOpenStudio={PROPOSAL_STUDIO_ENABLED ? openStudioForItem : undefined}
          comboVariationsSlot={
            tab.itens.length > 0 ? (
              <ComboVariationSection
                variations={tab.comboVariations}
                allItems={tab.itens}
                onChange={tab.setComboVariations}
                onExportPPTX={PROPOSAL_PPTX_EXPORT_ENABLED ? tab.handleExportVariationsPPTX : undefined}
                onExportPDF={PROPOSAL_PDF_EXPORT_ENABLED ? tab.handleExportVariationsPDF : undefined}
              />
            ) : null
          }
          footerExtra={
            PROPOSAL_FORM_ACTIONS_ENABLED
              ? tab.editingId
                ? (
                    <ExportSection
                      hasProposals
                      proposal={tab.proposals.find((p) => p.id === tab.editingId)}
                      onExportPPTX={PROPOSAL_PPTX_EXPORT_ENABLED ? tab.handleExportPPTX : undefined}
                      onExportPDF={PROPOSAL_PDF_EXPORT_ENABLED ? tab.handleExportPDF : undefined}
                    />
                  )
                : tab.proposals.length > 0
                  ? (
                      <ExportSection
                        hasProposals
                        proposal={tab.proposals[0]}
                        onExportPPTX={PROPOSAL_PPTX_EXPORT_ENABLED ? tab.handleExportPPTX : undefined}
                        onExportPDF={PROPOSAL_PDF_EXPORT_ENABLED ? tab.handleExportPDF : undefined}
                      />
                    )
                  : null
              : null
          }
        />
      )}

      {tab.editingItemIndex !== null && (
        <PlanEditor
          open
          catalogPlans={tab.catalogPlans}
          plan={tab.itens[tab.editingItemIndex]}
          onSave={(updatedItem) => {
            if (tab.editingItemIndex === null) return
            void tab.handlePlanItemUpdated(updatedItem, tab.editingItemIndex)
          }}
          onClose={() => tab.setEditingItemIndex(null)}
          readonly={!tab.canEdit}
          guided={catalogScope === 'rocket'}
        />
      )}

      {PROPOSAL_STUDIO_ENABLED && tab.studioPlan && tab.targetProposalForStudio && (
        <ProposalStudio
          deal={deal}
          initialItems={tab.targetProposalForStudio.itens}
          initialLayout={(tab.targetProposalForStudio as { layout?: LayoutConfig }).layout}
          initialComboVariations={parseComboVariationsFromDados(tab.targetProposalForStudio)}
          onClose={() => tab.setStudioPlan(null)}
          onSave={(savedItems, savedLayout, savedComboVariations) =>
            void tab.saveStudioPresentation(savedItems, savedLayout, savedComboVariations)
          }
        />
      )}

      {PROPOSAL_STUDIO_ENABLED && tab.studioOpen && (
        <ProposalStudio
          deal={deal}
          initialItems={tab.itens}
          initialComboVariations={tab.comboVariations}
          onClose={() => tab.setStudioOpen(false)}
          onSave={(savedItems, _savedLayout, savedComboVariations) => {
            tab.setItens(savedItems.map((i) => ({ ...i })))
            if (savedComboVariations) tab.setComboVariations(savedComboVariations)
            tab.setStudioOpen(false)
          }}
        />
      )}
    </div>
  )
}
